import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, Loader2, RefreshCw, Send } from 'lucide-react';
import { ClassPackBuilder } from '@/components/Admin/ClassPackBuilder';
import { ExpoAdmin } from '@/components/Admin/ExpoAdmin';
import { ClassProgramsAdmin } from '@/components/Admin/ClassProgramsAdmin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'ltbb-ba-token';
const ADMIN_TABS = ['classes', 'expo', 'packs', 'accounts'];
const readAdminTab = () => {
  const queryTab = new URLSearchParams(window.location.search).get('tab');
  if (ADMIN_TABS.includes(queryTab)) return queryTab;
  return ADMIN_TABS.includes(window.location.hash.slice(1)) ? window.location.hash.slice(1) : 'classes';
};
// Only student <-> facilitator moves here; the backend refuses every other role.
const ROLE_TOGGLE = {
  student: { to: 'facilitator', label: 'Make facilitator', confirm: email => `Make ${email} a facilitator? They get the class live view and will be signed out so the new role takes effect.` },
  facilitator: { to: 'student', label: 'Remove facilitator', confirm: email => `Remove facilitator access for ${email}? They go back to a student account and will be signed out.` },
};

export const AdminPanel = ({ onBack }) => {
  const [tab, setTab] = useState(readAdminTab);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventGroups, setEventGroups] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [storage, setStorage] = useState(null);
  const [exports, setExports] = useState([]);
  const [attempts, setAttempts] = useState({});
  const [pending, setPending] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setError('Not authenticated'); setLoading(false); return; }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, accountsRes, eventRes, storageRes, exportsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/v1/platform/admin/stats`, { headers }),
        fetch(`${BACKEND_URL}/api/v1/platform/admin/accounts?limit=100`, { headers }),
        fetch(`${BACKEND_URL}/api/v1/platform/admin/events`, { headers }),
        fetch(`${BACKEND_URL}/api/v1/platform/admin/storage`, { headers }),
        fetch(`${BACKEND_URL}/api/v1/platform/admin/exports?limit=100`, { headers }),
      ]);
      if ([statsRes, accountsRes, eventRes, storageRes, exportsRes].some(res => res.status === 403)) {
        setError('Admin access required'); setLoading(false); return;
      }
      if ([statsRes, accountsRes, eventRes, storageRes, exportsRes].some(res => !res.ok)) throw new Error('Admin data unavailable');
      const [data, accountData, eventData, storageData, exportData] = await Promise.all([
        statsRes.json(), accountsRes.json(), eventRes.json(), storageRes.json(), exportsRes.json(),
      ]);
      setStats(data.stats); setEvents(data.recent_events || []);
      setAccounts(accountData.accounts || []); setEventGroups(eventData.events || []); setStorage(storageData);
      setExports(exportData.exports || []);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); const i = setInterval(load, 60000); return () => clearInterval(i); }, []);
  useEffect(() => {
    if (!ADMIN_TABS.includes(window.location.hash.slice(1))) window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#classes`);
    const update = () => setTab(readAdminTab());
    window.addEventListener('hashchange', update);
    return () => window.removeEventListener('hashchange', update);
  }, []);
  const chooseTab = (next) => { window.location.hash = next; setTab(next); };

  const mutate = async (key, url, confirmText, payload) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setPending(p => ({ ...p, [key]: true }));
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` };
      if (payload) headers['Content-Type'] = 'application/json';
      const res = await fetch(`${BACKEND_URL}${url}`, { method: 'POST', headers, body: payload ? JSON.stringify(payload) : undefined });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || 'Action failed');
      await load();
    } catch (e) { setError(e.message); }
    finally { setPending(p => ({ ...p, [key]: false })); }
  };

  const showAttempts = async exportId => {
    const res = await fetch(`${BACKEND_URL}/api/v1/platform/admin/exports/${exportId}/resend-attempts`,
      { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
    if (res.ok) {
      const data = await res.json();
      setAttempts(a => ({ ...a, [exportId]: data.attempts }));
    }
  };

  const cards = stats ? [
    { label: 'Total Users', value: stats.total_users },
    { label: 'Signups (24h)', value: stats.signups_24h },
    { label: 'Signups (7d)', value: stats.signups_7d },
    { label: 'Signups (30d)', value: stats.signups_30d },
    { label: 'Failed Signups (24h)', value: stats.failed_signups_24h, alert: stats.failed_signups_24h > 10 },
    { label: 'Failed Logins (24h)', value: stats.failed_logins_24h, alert: stats.failed_logins_24h > 20 },
    { label: 'Save Blocked (24h)', value: stats.save_blocked_24h },
    { label: 'Export Emails (total)', value: stats.export_emails_total },
    { label: 'Export Emails (24h)', value: stats.export_emails_24h },
    { label: 'Active Beats', value: stats.active_beats },
    { label: 'Beats Exported (7d)', value: stats.beats_exported_7d },
  ] : [];

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FF0022' }}>{error}</div>
  );

  return (
    <div data-testid="admin-panel" style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff', fontFamily: "'Inter', sans-serif" }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
        <div className="flex items-center gap-3" style={{ marginBottom: 24 }}>
          {onBack && <button onClick={onBack} style={{ background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: 8, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', cursor: 'pointer' }}><ArrowLeft size={16} /></button>}
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Beat Arranger Admin</h1>
          <button onClick={() => { setLoading(true); load(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}><RefreshCw size={16} /></button>
          {loading && <Loader2 size={16} className="animate-spin" style={{ color: '#666' }} />}
        </div>

        <nav className="admin-tabs" aria-label="Admin sections">
          <button aria-current={tab === 'expo' ? 'page' : undefined} onClick={() => chooseTab('expo')}>Expo</button>
          <button aria-current={tab === 'classes' ? 'page' : undefined} onClick={() => chooseTab('classes')}>Classes</button>
          <button aria-current={tab === 'packs' ? 'page' : undefined} onClick={() => chooseTab('packs')}>Class Packs</button>
          <button aria-current={tab === 'accounts' ? 'page' : undefined} onClick={() => chooseTab('accounts')}>Accounts &amp; Stats</button>
        </nav>

        {tab === 'classes' && <ClassProgramsAdmin />}
        {tab === 'expo' && <ExpoAdmin />}
        {tab === 'packs' && <ClassPackBuilder />}
        {tab === 'accounts' && <>

        {stats && (
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', marginBottom: 32 }}>
            {cards.map(c => (
              <div key={c.label} style={{ background: '#1A1A1A', border: `1px solid ${c.alert ? '#FF002244' : '#2A2A2A'}`, borderRadius: 10, padding: '16px' }}>
                <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>{c.label}</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: c.alert ? '#FF0022' : '#fff' }}>{c.value}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: '#aaa', margin: 0 }}>Workshop Events</h2>
          <a href={`${BACKEND_URL}/api/v1/platform/admin/exports.csv`} onClick={async e => {
            e.preventDefault();
            const token = localStorage.getItem(TOKEN_KEY);
            const res = await fetch(e.currentTarget.href, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) return setError('CSV export failed');
            const url = URL.createObjectURL(await res.blob());
            const link = document.createElement('a'); link.href = url; link.download = 'beat-arranger-guest-exports.csv'; link.click();
            URL.revokeObjectURL(url);
          }} style={{ color: '#00D4AA', fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}><Download size={14} />Export CSV</a>
        </div>
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', marginBottom: 28 }}>
          {eventGroups.map(group => <div key={group.event || 'untagged'} style={{ background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: 8, padding: 12 }}>
            <div style={{ color: '#aaa', fontSize: 12 }}>{group.event || '(untagged)'}</div>
            <div style={{ fontWeight: 800, fontSize: 22 }}>{group.session_count}</div>
          </div>)}
        </div>

        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: '#aaa' }}>Accounts</h2>
        <div style={{ overflowX: 'auto', marginBottom: 28 }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ borderBottom: '1px solid #2A2A2A' }}>{['Email', 'Name', 'Role', 'Beats', 'Last active', 'Status', 'Action'].map(h => <th key={h} style={{ padding: 8, textAlign: 'left', color: '#666' }}>{h}</th>)}</tr></thead>
          <tbody>{accounts.map(account => <tr key={account.id} style={{ borderBottom: '1px solid #1A1A1A' }}>
            <td style={{ padding: 8 }}>{account.email}</td><td style={{ padding: 8 }}>{account.display_name}</td>
            <td style={{ padding: 8 }}>{account.role}</td><td style={{ padding: 8 }}>{account.project_count}</td>
            <td style={{ padding: 8 }}>{account.last_active_at ? new Date(account.last_active_at).toLocaleString() : '-'}</td>
            <td style={{ padding: 8 }}>{account.is_active ? 'Active' : 'Deactivated'}</td>
            <td style={{ padding: 8 }}><button disabled={pending[`account-${account.id}`]} onClick={() => mutate(`account-${account.id}`, `/api/v1/platform/admin/accounts/${account.id}/${account.is_active ? 'deactivate' : 'reactivate'}`, account.is_active ? `Deactivate ${account.email}? Their current tokens will stop working immediately.` : null)}>{account.is_active ? 'Deactivate' : 'Reactivate'}</button>
              {ROLE_TOGGLE[account.role] && <>{' '}<button data-testid={`role-toggle-${account.id}`} disabled={pending[`role-${account.id}`]} onClick={() => mutate(`role-${account.id}`, `/api/v1/platform/admin/accounts/${account.id}/role`, ROLE_TOGGLE[account.role].confirm(account.email), { role: ROLE_TOGGLE[account.role].to })}>{ROLE_TOGGLE[account.role].label}</button></>}</td>
          </tr>)}</tbody>
        </table></div>

        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: '#aaa' }}>Guest Exports</h2>
        <div style={{ overflowX: 'auto', marginBottom: 28 }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr>{['Recipient', 'Export state', 'Latest resend', 'Actions'].map(h => <th key={h} style={{ padding: 8, textAlign: 'left', color: '#666' }}>{h}</th>)}</tr></thead>
          <tbody>{exports.map(item => <React.Fragment key={item.id}><tr style={{ borderBottom: '1px solid #1A1A1A' }}>
            <td style={{ padding: 8 }}>{item.recipient_email}</td><td style={{ padding: 8 }}>{item.state}</td>
            <td style={{ padding: 8 }}>{item.latest_resend_attempt?.status || 'None'}</td>
            <td style={{ padding: 8 }}><button title="Eligibility is provisional until storage is checked" disabled={!item.resend_eligible_provisional || pending[`export-${item.id}`] || ['claimed','submitted','unknown'].includes(item.latest_resend_attempt?.status)} onClick={() => mutate(`export-${item.id}`, `/api/v1/platform/admin/exports/${item.id}/resend`)}><Send size={12} /> Resend</button>{' '}<button onClick={() => showAttempts(item.id)}>History</button></td>
          </tr>{attempts[item.id] && <tr><td colSpan={4} style={{ padding: 8, color: '#aaa' }}>{attempts[item.id].map(a => <div key={a.id}>{a.status === 'abandoned' ? 'Abandoned (superseded before submission)' : a.status} · {new Date(a.created_at).toLocaleString()}</div>)}</td></tr>}</React.Fragment>)}</tbody>
        </table></div>

        {storage && <div style={{ background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: 10, padding: 16, marginBottom: 28, fontSize: 12, color: '#aaa' }}>
          Storage: SQLite {storage.sqlite.available ? `${(storage.sqlite.bytes / 1048576).toFixed(2)} MiB` : 'unavailable'} · R2 {storage.r2.available ? `${storage.r2.scanned_object_count} objects / ${(storage.r2.scanned_bytes / 1048576).toFixed(2)} MiB${storage.r2.is_lower_bound ? ' (lower bound)' : ''}` : 'measurement pending or unavailable'}
        </div>}

        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: '#aaa' }}>Recent Events</h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2A2A2A' }}>
                <th style={{ padding: '8px', textAlign: 'left', color: '#666', fontWeight: 600 }}>Type</th>
                <th style={{ padding: '8px', textAlign: 'left', color: '#666', fontWeight: 600 }}>Email</th>
                <th style={{ padding: '8px', textAlign: 'left', color: '#666', fontWeight: 600 }}>Reason</th>
                <th style={{ padding: '8px', textAlign: 'left', color: '#666', fontWeight: 600 }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #1A1A1A' }}>
                  <td style={{ padding: '8px', color: e.event_type?.includes('FAIL') ? '#FF0022' : '#00D4AA' }}>{e.event_type}</td>
                  <td style={{ padding: '8px', color: '#aaa' }}>{e.email || '-'}</td>
                  <td style={{ padding: '8px', color: '#666' }}>{e.reason || '-'}</td>
                  <td style={{ padding: '8px', color: '#666' }}>{e.timestamp ? new Date(e.timestamp).toLocaleString() : '-'}</td>
                </tr>
              ))}
              {events.length === 0 && <tr><td colSpan={4} style={{ padding: 16, textAlign: 'center', color: '#444' }}>No events yet</td></tr>}
            </tbody>
          </table>
        </div>
        </>}
      </div>
    </div>
  );
};

export default AdminPanel;
